import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import { getDatabase, ref, onValue, off } from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

// ─── Firebase ─────────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
    authDomain:        'clubswap-5a74a.firebaseapp.com',
    databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
    projectId:         'clubswap-5a74a',
    storageBucket:     'clubswap-5a74a.firebasestorage.app',
    messagingSenderId: '274838532963',
    appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
}

let _app, _db, _auth
function getDB() {
    if (!_db) {
        _app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        _db   = getDatabase(_app)
        _auth = getAuth(_app)
    }
    return _db
}

// ─── Constants ────────────────────────────────────────────────────────────────
const WINDOW_MS = 48 * 60 * 60 * 1000  // 48 hours

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(ts) {
    if (!ts) return ''
    const diff = Date.now() - ts
    const m = Math.floor(diff / 60000)
    if (m < 1) return 'just now'
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    return `${Math.floor(h / 24)}d ago`
}

function expiresIn(ts) {
    const expiresAt = ts + WINDOW_MS
    const left = expiresAt - Date.now()
    if (left <= 0) return 'expiring soon'
    const h = Math.floor(left / 3600000)
    const m = Math.floor((left % 3600000) / 60000)
    if (h === 0) return `disappears in ${m}m`
    return `disappears in ${h}h ${m}m`
}

function notifIcon(type) {
    const icons = {
        challenge_received:  '⚔️',
        challenge_accepted:  '✅',
        challenge_declined:  '❌',
        challenge_expired:   '⏰',
        playing:             '🎮',
        result_submitted:    '🏁',
        win:                 '🏆',
        loss:                '💔',
        settled:             '💎',
        disputed:            '⚠️',
        dispute_resolved:    '✅',
        tournament_started:  '🏆',
        tournament_joined:   '👥',
        tournament_result:   '🎯',
        refund:              '↩️',
    }
    return icons[type] || '📣'
}

function notifColor(type) {
    if (['win', 'settled', 'challenge_accepted', 'dispute_resolved'].includes(type)) return '#4ade80'
    if (['loss', 'challenge_declined', 'disputed'].includes(type)) return '#f87171'
    if (['challenge_received', 'playing', 'tournament_started'].includes(type)) return '#a78bfa'
    if (['challenge_expired', 'refund'].includes(type)) return '#fbbf24'
    return '#4a9aca'
}

// ─────────────────────────────────────────────────────────────────────────────
//  BUILD NOTIFICATIONS from Firebase data
// ─────────────────────────────────────────────────────────────────────────────
function buildNotifications(uid, challenges, tournaments) {
    const notifs = []
    const cutoff = Date.now() - WINDOW_MS

    // ── From challenges ───────────────────────────────────────
    Object.entries(challenges || {}).forEach(([cid, c]) => {
        const isChallenger = c.challengerUid === uid
        const isOpponent   = c.opponentUid === uid
        if (!isChallenger && !isOpponent) return

        const oppName = isChallenger ? c.opponentName : c.challengerName
        const myName  = isChallenger ? c.challengerName : c.opponentName
        const game    = c.gameType === 'pool' ? '🎱 8 Ball Pool' : '♟️ Checkers'

        // Challenge created (opponent sees)
        if (isOpponent && c.createdAt > cutoff) {
            notifs.push({
                id: `${cid}_created`,
                type: 'challenge_received',
                title: `${c.challengerName} challenged you!`,
                body: `${game} · 💎 ${c.stake} stake`,
                link: `/challenge/${cid}`,
                ts: c.createdAt,
            })
        }

        // Accepted
        if (isChallenger && c.acceptedAt > cutoff) {
            notifs.push({
                id: `${cid}_accepted`,
                type: 'challenge_accepted',
                title: `${oppName} accepted your challenge!`,
                body: `${game} · Get the room code ready`,
                link: `/challenge/${cid}`,
                ts: c.acceptedAt,
            })
        }

        // Playing
        if (c.playingAt > cutoff && (isChallenger || isOpponent)) {
            notifs.push({
                id: `${cid}_playing`,
                type: 'playing',
                title: 'Match started!',
                body: `${c.challengerName} vs ${c.opponentName} · ${game}`,
                link: `/challenge/${cid}`,
                ts: c.playingAt,
            })
        }

        // Result submitted (other player)
        if (c.resultSubmittedAt > cutoff) {
            const submitter = c.challengerResult && isOpponent ? c.challengerName
                : c.opponentResult && isChallenger ? c.opponentName : null
            if (submitter) {
                notifs.push({
                    id: `${cid}_result`,
                    type: 'result_submitted',
                    title: `${submitter} submitted a result`,
                    body: 'Open the app to confirm or dispute',
                    link: `/challenge/${cid}`,
                    ts: c.resultSubmittedAt,
                })
            }
        }

        // Settled
        if (c.settledAt > cutoff && (isChallenger || isOpponent)) {
            const iWon = c.winner === uid
            notifs.push({
                id: `${cid}_settled`,
                type: iWon ? 'win' : 'loss',
                title: iWon ? `You won! 🏆` : `Match lost`,
                body: iWon
                    ? `+${c.payout} 💎 added to your balance`
                    : `Better luck next time against ${oppName}`,
                link: `/challenge/${cid}`,
                ts: c.settledAt,
            })
        }

        // Disputed
        if (c.disputedAt > cutoff && (isChallenger || isOpponent)) {
            notifs.push({
                id: `${cid}_disputed`,
                type: 'disputed',
                title: 'Match under review',
                body: 'A dispute was raised. Submit your evidence.',
                link: c.disputeId ? `/dispute/${c.disputeId}` : `/challenge/${cid}`,
                ts: c.disputedAt,
            })
        }

        // Expired / declined / cancelled
        if (isChallenger && ['expired', 'declined', 'cancelled'].includes(c.status)) {
            const ts = c.disputedAt || c.createdAt
            if (ts > cutoff) {
                notifs.push({
                    id: `${cid}_${c.status}`,
                    type: 'challenge_declined',
                    title: c.status === 'expired' ? 'Challenge expired'
                        : c.status === 'declined' ? `${oppName} declined your challenge`
                        : 'Challenge cancelled',
                    body: 'Full refund has been issued.',
                    link: '/',
                    ts,
                })
            }
        }
    })

    // ── From tournaments ──────────────────────────────────────
    Object.entries(tournaments || {}).forEach(([tid, t]) => {
        const meta    = t.meta || {}
        const players = t.players || {}
        const isInvolved = !!players[uid] || meta.hostUid === uid
        if (!isInvolved) return

        if (meta.startedAt > cutoff) {
            notifs.push({
                id: `${tid}_started`,
                type: 'tournament_started',
                title: `${meta.name} has started!`,
                body: `${meta.playerCount || Object.keys(players).length} players · 🎥 Start recording!`,
                link: `/tournament/${tid}`,
                ts: meta.startedAt,
            })
        }

        if (meta.announcedAt > cutoff) {
            const iWon = meta.winners && Object.values(meta.winners).includes(uid)
            notifs.push({
                id: `${tid}_announced`,
                type: iWon ? 'win' : 'tournament_result',
                title: iWon ? 'You placed in the tournament! 🏆' : 'Tournament results announced',
                body: iWon ? 'Check your balance for the payout!' : `View the final standings`,
                link: `/tournament/${tid}`,
                ts: meta.announcedAt,
            })
        }

        if (meta.disputedAt > cutoff) {
            notifs.push({
                id: `${tid}_disputed`,
                type: 'disputed',
                title: `${meta.name} is under review`,
                body: 'Conflicting claims. Submit your evidence.',
                link: meta.disputeId ? `/dispute/${meta.disputeId}` : `/tournament/${tid}`,
                ts: meta.disputedAt,
            })
        }

        if (meta.settledAt > cutoff) {
            notifs.push({
                id: `${tid}_settled`,
                type: 'settled',
                title: `${meta.name} settled`,
                body: 'Payouts have been released. Check your balance.',
                link: `/tournament/${tid}`,
                ts: meta.settledAt,
            })
        }
    })

    // Sort newest first, deduplicate
    const seen = new Set()
    return notifs
        .filter(n => { if (seen.has(n.id)) return false; seen.add(n.id); return true })
        .sort((a, b) => b.ts - a.ts)
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Notifications() {
    const navigate = useNavigate()
    const [currentUser, setCurrentUser] = useState(null)
    const [authReady, setAuthReady]     = useState(false)
    const [notifs, setNotifs]           = useState([])
    const [loading, setLoading]         = useState(true)

    // ── Auth ──────────────────────────────────────────────────
    useEffect(() => {
        const db   = getDB()
        const auth = getAuth(getApps()[0])
        const unsub = onAuthStateChanged(auth, user => {
            setCurrentUser(user)
            setAuthReady(true)
            if (!user) setLoading(false)
        })
        return () => unsub()
    }, [])

    // ── Load data ─────────────────────────────────────────────
    useEffect(() => {
        if (!currentUser) return
        const db = getDB()

        let challenges  = {}
        let tournaments = {}
        let loaded      = 0

        const rebuild = () => {
            loaded++
            if (loaded >= 2) {
                setNotifs(buildNotifications(currentUser.uid, challenges, tournaments))
                setLoading(false)
            }
        }

        const chalUnsub = onValue(ref(db, 'challenges'), snap => {
            challenges = snap.val() || {}
            rebuild()
        })
        const tUnsub = onValue(ref(db, 'tournaments'), snap => {
            tournaments = snap.val() || {}
            rebuild()
        })

        return () => {
            off(ref(db, 'challenges'))
            off(ref(db, 'tournaments'))
        }
    }, [currentUser])

    // ─────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────
    if (!authReady || loading) {
        return (
            <div style={n.root}>
                <div style={n.header}>
                    <button style={n.backBtn} onClick={() => navigate(-1)}>←</button>
                    <span style={n.headerTitle}>Notifications</span>
                </div>
                <div style={n.centered}><div style={n.spinner} /></div>
            </div>
        )
    }

    if (!currentUser) {
        return (
            <div style={n.root}>
                <div style={n.header}>
                    <button style={n.backBtn} onClick={() => navigate(-1)}>←</button>
                    <span style={n.headerTitle}>Notifications</span>
                </div>
                <div style={n.centered}>
                    <div style={n.emptyIcon}>🔔</div>
                    <div style={n.emptyTitle}>Sign in to see notifications</div>
                    <button style={n.actionBtn} onClick={() => navigate('/account')}>Sign In</button>
                </div>
            </div>
        )
    }

    return (
        <div style={n.root}>
            {/* Header */}
            <div style={n.header}>
                <button style={n.backBtn} onClick={() => navigate(-1)}>←</button>
                <div>
                    <span style={n.headerTitle}>Notifications</span>
                    {notifs.length > 0 && (
                        <span style={n.headerCount}>{notifs.length}</span>
                    )}
                </div>
                <div style={n.headerNote}>Last 48 hours</div>
            </div>

            {/* Disclaimer */}
            <div style={n.disclaimerBar}>
                <span style={n.disclaimerIcon}>⏰</span>
                <span style={n.disclaimerText}>
                    Notifications automatically disappear after 48 hours
                </span>
            </div>

            {/* List */}
            {notifs.length === 0 ? (
                <div style={n.centered}>
                    <div style={n.emptyIcon}>🔔</div>
                    <div style={n.emptyTitle}>No notifications yet</div>
                    <div style={n.emptySub}>
                        Your challenges, match results and tournament updates will appear here.
                    </div>
                </div>
            ) : (
                <div style={n.list}>
                    {notifs.map((notif, i) => {
                        const color = notifColor(notif.type)
                        const pct   = Math.max(0, Math.min(100, ((notif.ts + WINDOW_MS - Date.now()) / WINDOW_MS) * 100))
                        return (
                            <div
                                key={notif.id}
                                style={n.notifCard}
                                onClick={() => navigate(notif.link)}
                            >
                                {/* Left accent */}
                                <div style={{ ...n.accent, background: color }} />

                                {/* Icon */}
                                <div style={{ ...n.iconWrap, background: color + '22', border: `1px solid ${color}44` }}>
                                    <span style={n.icon}>{notifIcon(notif.type)}</span>
                                </div>

                                {/* Content */}
                                <div style={n.notifContent}>
                                    <div style={n.notifTitle}>{notif.title}</div>
                                    <div style={n.notifBody}>{notif.body}</div>

                                    {/* Expiry bar */}
                                    <div style={n.expiryRow}>
                                        <div style={n.expiryBarWrap}>
                                            <div style={{ ...n.expiryBar, width: `${pct}%`, background: color }} />
                                        </div>
                                        <span style={{ ...n.expiryLabel, color }}>
                                            {expiresIn(notif.ts)}
                                        </span>
                                    </div>
                                </div>

                                {/* Time + arrow */}
                                <div style={n.notifRight}>
                                    <span style={n.timeAgo}>{timeAgo(notif.ts)}</span>
                                    <span style={{ ...n.arrow, color }}>→</span>
                                </div>
                            </div>
                        )
                    })}

                    <div style={n.footerNote}>
                        All notifications expire 48 hours after they are generated.
                        They are not stored permanently.
                    </div>
                </div>
            )}
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const n = {
    root: {
        minHeight: '100vh',
        background: '#071309',
        color: '#e8f5e9',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '16px 20px',
        borderBottom: '1px solid #0d2210',
        background: '#060f08',
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    backBtn: {
        background: 'none',
        border: 'none',
        color: '#4ade80',
        fontSize: 20,
        cursor: 'pointer',
        padding: '4px 8px',
        flexShrink: 0,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: 800,
        color: '#e8f5e9',
    },
    headerCount: {
        marginLeft: 8,
        fontSize: 11,
        fontWeight: 700,
        color: '#4ade80',
        background: '#0a2a0c',
        border: '1px solid #1a4a1c',
        padding: '2px 8px',
        borderRadius: 20,
    },
    headerNote: {
        marginLeft: 'auto',
        fontSize: 11,
        color: '#2a5a2c',
    },

    disclaimerBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 20px',
        background: '#0a1a0c',
        borderBottom: '1px solid #0d2210',
    },
    disclaimerIcon: { fontSize: 14 },
    disclaimerText: { fontSize: 12, color: '#2a5a2c' },

    centered: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: 12,
        padding: 24,
        textAlign: 'center',
    },
    emptyIcon:  { fontSize: 52, marginBottom: 4 },
    emptyTitle: { fontSize: 17, fontWeight: 700, color: '#e8f5e9' },
    emptySub:   { fontSize: 13, color: '#2a5a2c', lineHeight: 1.6, maxWidth: 280 },
    actionBtn:  {
        padding: '11px 24px',
        borderRadius: 10,
        background: '#4ade80',
        border: 'none',
        color: '#060f08',
        fontWeight: 800,
        fontSize: 14,
        cursor: 'pointer',
        marginTop: 8,
    },

    list: {
        padding: '12px 16px 80px',
        maxWidth: 600,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    notifCard: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        background: '#0a1a0c',
        border: '1px solid #0d2210',
        borderRadius: 14,
        padding: '14px 14px 14px 0',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.15s',
    },

    accent: {
        width: 3,
        alignSelf: 'stretch',
        borderRadius: '0 2px 2px 0',
        flexShrink: 0,
        minHeight: 60,
    },

    iconWrap: {
        width: 38,
        height: 38,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    icon: { fontSize: 18 },

    notifContent: { flex: 1, minWidth: 0 },
    notifTitle:   { fontSize: 13, fontWeight: 700, color: '#e8f5e9', marginBottom: 3 },
    notifBody:    { fontSize: 12, color: '#3a6a3c', lineHeight: 1.4, marginBottom: 8 },

    expiryRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    expiryBarWrap: {
        flex: 1,
        height: 3,
        background: '#0d2210',
        borderRadius: 2,
        overflow: 'hidden',
    },
    expiryBar: {
        height: '100%',
        borderRadius: 2,
        transition: 'width 60s linear',
        opacity: 0.7,
    },
    expiryLabel: {
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },

    notifRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
        flexShrink: 0,
        paddingRight: 4,
    },
    timeAgo: { fontSize: 10, color: '#2a5a2c', whiteSpace: 'nowrap' },
    arrow:   { fontSize: 14, fontWeight: 700 },

    footerNote: {
        fontSize: 11,
        color: '#1a3a1c',
        textAlign: 'center',
        padding: '16px 0 8px',
        lineHeight: 1.5,
    },

    spinner: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: '3px solid #0d2210',
        borderTop: '3px solid #4ade80',
    },
}
